import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import * as firebaseMessaging from '@firebase/messaging';
import * as Sentry from '@sentry/browser';
import * as firebaseApp from 'firebase/app';
import * as firebaseAuth from 'firebase/auth';
import { updateFirebaseCurrentUser } from 'src/api/capacitor-adapter';
import { SplashScreen } from 'src/components/splash-screen';
import {
  useNotificationsContext,
  useTenantContext
} from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import { useMountEffect } from 'src/hooks/use-mount-effect';
import { messageServiceWorker } from 'src/libs/sw-registration';
import { AuthContextType, addSession, getSession } from './auth-context';
import {
  FirebaseState,
  firebaseInstance,
  initializeFirebase
} from '../../libs/firebase';

/**
 * Provides simplified firebase callbacks
 */
export interface FirebaseCallbacks {
  signInWithEmailLink: (email: string, url: string) => Promise<void>;
  signInWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<void>;
  signOut: (reason?: string) => Promise<void>;
  // user management
  updatePassword: (current: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resetPasswordWithEmailLink: (
    oobCode: string,
    password: string
  ) => Promise<void>;
  validateOobCode: (oobCode: string) => Promise<void>;
  // development tools
  saveSession: (storageId: string, sessionName: string) => void;
  loadSession: (storage: string) => void;
}

/**
 * Provides user authentication state
 */
export interface FirebaseAuthState {
  // login state
  initialized: boolean;
  authenticated: boolean;
  failed: boolean;
  // firebase user (provided for re-renders)
  firebaseUser?: firebaseAuth.User;
  tokenResult?: firebaseAuth.IdTokenResult;
}

const initialState: FirebaseAuthState = {
  initialized: false,
  authenticated: false,
  failed: false
};

export type FirebaseContextType = AuthContextType &
  FirebaseCallbacks &
  FirebaseAuthState;

export const FirebaseContext = createContext<FirebaseContextType | undefined>(
  undefined
);

/**
 * Resolve the authentication state
 */
const handleFirebaseAuthChanged = async (
  firebaseUser: firebaseAuth.User | null
) => {
  console.info('[firebase] Authentication changed:', !!firebaseUser);

  if (!firebaseUser)
    return {
      initialized: true,
      authenticated: false,
      failed: false
    };

  try {
    const tokenResult = await firebaseUser?.getIdTokenResult();

    return {
      initialized: true,
      authenticated: true,
      failed: false,
      firebaseUser,
      tokenResult
    };
  } catch (error) {
    // ensure firebase error is logged
    console.error(error);

    return {
      initialized: true,
      authenticated: false,
      failed: true
    };
  }
};

/**
 * Helper to improve authentication debugging
 */
const withDebugContext = async <T extends any>(
  firebase: FirebaseState | undefined,
  debugMessage: string,
  debugExtra: Record<string, unknown>,
  callback: (auth: firebaseAuth.Auth) => Promise<T>
): Promise<void> => {
  console.info(`[firebase] ${debugMessage}`);
  if (!firebase) throw new Error('Firebase not initialized');
  await callback(firebase.auth).catch((error) => {
    // ensure the error is properly captured with Sentry
    // (this is critical for login error debugging)
    Sentry.captureException(error, {
      extra: {
        ...debugExtra,
        currentTenantId: firebase.auth.tenantId
      }
    });
    // re-throw the error to the caller
    throw error;
  });
};

/**
 * Provide the firebase logic to children
 *
 * NOTE: This component blocks rendering until the firebase context has been
 * initialized properly. Children can rely on the context being ready.
 *
 * There is no generic auth context, because some authentication actions are
 * provider specific and a useful abstraction is only possible once another
 * provider is concretely deployed.
 */
export const FirebaseProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const [state, setState] = useState<FirebaseAuthState>(initialState);
  const { tenant } = useTenantContext();
  const { handleRequestPermissions } = useNotificationsContext();

  const handleState = useCallback(async (state: FirebaseAuthState) => {
    setState(state);
    // provide the token to the service worker
    await messageServiceWorker('FIREBASE_TOKEN', state.tokenResult);
  }, []);

  const handleTenant = useCallback((tenantId?: string) => {
    // update tenant in the current instance
    if (firebaseInstance) {
      console.info('[firebase] Tenant changed:', tenantId ?? 'none');
      firebaseInstance.auth.tenantId = tenantId || null;
    }

    // provide tenant to Sentry when available
    Sentry.setTag('tenant', tenantId || 'unknown');
  }, []);

  // initialize firebase
  // (returns a local copy which can be used to track changes)
  useMountEffect(async () => {
    console.info('[firebase] Initialization');
    if (!globalThis.__FIREBASE__)
      throw new Error('[firebase] Missing configuration');

    const app = firebaseApp.initializeApp(globalThis.__FIREBASE__);
    const auth = firebaseAuth.getAuth();
    const messaging = (await firebaseMessaging.isSupported())
      ? firebaseMessaging.getMessaging(app)
      : undefined;

    // initialize global firebase instance
    // (later tenant changes are handled by an effect hook)
    initializeFirebase(
      {
        app,
        auth,
        messaging
      },
      tenant?.tenantId ?? null
    );

    firebaseAuth.setPersistence(auth, firebaseAuth.browserLocalPersistence);
    firebaseAuth.onIdTokenChanged(auth, async (user) => {
      console.info(`[firebase] Token changed`, !!user);
      // inject the user into the API (no re-render)
      updateFirebaseCurrentUser(user);
      // update the local state (and cause re-render after API is ready)
      await handleFirebaseAuthChanged(user).then(handleState);
      // request notifications when authentication changes
      if (!!user) await handleRequestPermissions('Firebase auth changed');
    });

    // ensure initial login is completed
    await auth.authStateReady();
    // initialize remaining state
    return firebaseInstance;
  });

  useEffect(
    () => handleTenant(tenant?.tenantId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenant?.tenantId]
  );

  const signInWithEmailAndPassword = useCallback(
    (email: string, password: string): Promise<void> =>
      withDebugContext(
        firebaseInstance,
        'Sign in with email/password',
        { email },
        (auth) => firebaseAuth.signInWithEmailAndPassword(auth, email, password)
      ),
    []
  );

  const signInWithEmailLink = useCallback(
    (email: string, url: string): Promise<void> =>
      withDebugContext(
        firebaseInstance,
        'Sign in with email/link',
        { email },
        (auth) => firebaseAuth.signInWithEmailLink(auth, email, url)
      ),
    []
  );

  const signOut = useCallback(
    (): Promise<void> =>
      withDebugContext(firebaseInstance, 'Sign out', {}, (auth) =>
        firebaseAuth.signOut(auth)
      ),
    []
  );

  const resetPassword = useCallback(
    (email: string): Promise<void> =>
      withDebugContext(firebaseInstance, 'Reset password', { email }, (auth) =>
        firebaseAuth.sendPasswordResetEmail(auth, email)
      ),
    []
  );

  const updatePassword = useCallback(
    async (current: string, password: string): Promise<void> => {
      const user = firebaseInstance?.auth.currentUser;
      if (!user?.email) return;

      // changing password requires recent re-authentication
      const credential = firebaseAuth.EmailAuthProvider.credential(
        user.email,
        current
      );

      console.info('[firebase] Update password');
      await firebaseAuth.reauthenticateWithCredential(user, credential);
      await firebaseAuth.updatePassword(user, password);
    },
    []
  );

  const resetPasswordWithEmailLink = useCallback(
    (oobCode: string, password: string) =>
      withDebugContext(
        firebaseInstance,
        'Reset password with email/link',
        { oobCode },
        (auth) => firebaseAuth.confirmPasswordReset(auth, oobCode, password)
      ),
    []
  );

  const validateOobCode = useCallback(
    (oobCode: string): Promise<void> =>
      withDebugContext(
        firebaseInstance,
        'Validate OOB code',
        { oobCode },
        (auth) => firebaseAuth.verifyPasswordResetCode(auth, oobCode)
      ),
    []
  );

  const saveSession = useCallback(
    (storageId: string, sessionName: string): void => {
      const apiKey = globalThis.__FIREBASE__?.apiKey;

      // retrieve the underlying firebase token
      const key = `firebase:authUser:${apiKey}:[DEFAULT]`;
      const session = localStorage.getItem(key);
      addSession(storageId, sessionName, session);
    },
    []
  );

  const loadSession = useCallback((storageId: string): void => {
    const apiKey = globalThis.__FIREBASE__?.apiKey;

    // restore the underlying firebase token and reload
    const session = getSession(storageId);
    const key = `firebase:authUser:${apiKey}:[DEFAULT]`;
    localStorage.setItem(key, session.session);

    // activate the login
    window.location.reload();
  }, []);

  const context = useHookMemo({
    // auth state
    initialized: state.initialized,
    authenticated: state.authenticated,
    failed: state.failed,
    firebaseUser: state.firebaseUser,
    tokenResult: state.tokenResult,
    // firebase callbacks
    signInWithEmailAndPassword,
    signInWithEmailLink,
    signOut,
    updatePassword,
    resetPassword,
    resetPasswordWithEmailLink,
    validateOobCode,
    // development tools
    saveSession,
    loadSession
  });

  // children rely on valid context
  if (state.failed) return <SplashScreen variant="error" />;
  if (!state.initialized) return <SplashScreen />;

  return (
    <FirebaseContext.Provider value={context}>
      {children}
    </FirebaseContext.Provider>
  );
};
